import { urlApi } from './api'
import { ecrireReglage, lireReglage, remplacerZonage } from './db'
import type { SecteurZonage } from '../domaine/types'

/*
  Ouverture de session.

  Aucun compte utilisateur. Aucun email, aucun nom de famille, aucun mot de
  passe. Un code de saison partagé, saisi une fois, échangé contre un jeton
  d'appareil opaque. Un prénom libre et facultatif, uniquement pour identifier
  le binôme sur le terrain.
*/

export type ResultatSession =
  | { ok: true }
  | { ok: false; motif: 'code_invalide' }
  | { ok: false; motif: 'trop_de_tentatives'; reessayerDans: number }
  | { ok: false; motif: 'reseau' }

interface ReponseSession {
  device_token: string
  saison: { id: number; libelle: string }
  zonage: { version: number; secteurs: SecteurZonage[] }
}

export async function ouvrirSession(
  code: string,
  prenom: string | null,
): Promise<ResultatSession> {
  let reponse: Response
  try {
    reponse = await fetch(urlApi('session'), {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: code.trim(), prenom: prenom?.trim() || null }),
    })
  } catch {
    return { ok: false, motif: 'reseau' }
  }

  if (reponse.status === 401) return { ok: false, motif: 'code_invalide' }

  if (reponse.status === 429) {
    const corps = (await reponse.json().catch(() => ({}))) as { reessayer_dans?: number }
    return {
      ok: false,
      motif: 'trop_de_tentatives',
      reessayerDans: corps.reessayer_dans ?? 900,
    }
  }

  if (!reponse.ok) return { ok: false, motif: 'reseau' }

  const donnees = (await reponse.json()) as ReponseSession

  await ecrireReglage('device_token', donnees.device_token)
  await ecrireReglage('saison_id', donnees.saison.id)
  await ecrireReglage('saison_libelle', donnees.saison.libelle)
  if (prenom?.trim()) await ecrireReglage('prenom', prenom.trim())

  // Le zonage arrive dans la même réponse : un seul aller-retour au premier
  // lancement, et l'application fonctionne hors réseau ensuite.
  await remplacerZonage(donnees.zonage.secteurs, donnees.zonage.version)

  return { ok: true }
}

export async function sessionOuverte(): Promise<boolean> {
  return (await lireReglage('device_token')) !== undefined
}
